import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

type GemType = '🍎' | '🍊' | '🍇' | '🍓' | '🥝' | '🍒' | '🍑' | '🥭';
type PowerUpType = '💣' | '⚡' | '🌟' | '🔥' | '💎' | '🌈' | '🎯' | '⚔️';
type Gem = {
  id: string;
  type: GemType | PowerUpType;
  x: number;
  y: number;
  isMatched: boolean;
  isPowerUp: boolean;
  powerUpType?: PowerUpType;
  originalType?: GemType;
  isFalling: boolean;
  isNew: boolean;
  matchCount: number;
};

const BOARD_SIZE = 7; // 7x7 Board für bessere Übersicht
const GEM_TYPES: GemType[] = ['🍎', '🍊', '🍇', '🍓', '🥝', '🍒', '🍑', '🥭'];

interface MatchThreeGameProps {
  isOpen: boolean;
  onClose: () => void;
  level: number;
}

export const MatchThreeGame: React.FC<MatchThreeGameProps> = ({ isOpen, onClose, level }) => {
  const { completeMatchThreeLevel, addGems, addCoins } = useGameStore();
  
  const [board, setBoard] = useState<Gem[][]>([]);
  const [selectedGem, setSelectedGem] = useState<{x: number, y: number} | null>(null);
  const [score, setScore] = useState(0);
  
  // Dynamische Zuganzahl basierend auf Level
  const maxMoves = (() => {
    if (level <= 5) {
      return 25; // Level 1-5: 25 Züge
    } else if (level <= 10) {
      return 22; // Level 6-10: 22 Züge
    } else if (level <= 20) {
      return 20; // Level 11-20: 20 Züge
    } else if (level <= 30) {
      return 18; // Level 21-30: 18 Züge
    } else {
      return 15; // Level 31+: 15 Züge
    }
  })();
  
  const [moves, setMoves] = useState(maxMoves);
  
  // Dynamische Zielpunkte basierend auf Level (angepasst für 7x7 Board)
  const targetScore = (() => {
    if (level <= 5) {
      return level * 600; // Level 1-5: 600, 1200, 1800, 2400, 3000
    } else if (level <= 10) {
      return 3000 + (level - 5) * 900; // Level 6-10: 3900, 4800, 5700, 6600, 7500
    } else if (level <= 20) {
      return 7500 + (level - 10) * 1350; // Level 11-20: 8850, 10200, 11550, 12900, 14250, 15600, 16950, 18300, 19650, 21000
    } else if (level <= 30) {
      return 21000 + (level - 20) * 1875; // Level 21-30: 22875, 24750, 26625, 28500, 30375, 32250, 34125, 36000, 37875, 39750
    } else {
      return 39750 + (level - 30) * 2625; // Level 31+: 42375, 45000, 47625, ...
    }
  })();
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [particles, setParticles] = useState<Array<{
    id: string, 
    x: number, 
    y: number, 
    type: string, 
    life: number,
    color: string,
    size: string,
    speed: number,
    angle: number
  }>>([]);
  
  // Moderne Touch/Drag States
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{x: number, y: number, clientX: number, clientY: number} | null>(null);
  const [dragEnd, setDragEnd] = useState<{x: number, y: number, clientX: number, clientY: number} | null>(null);
  const [dragDirection, setDragDirection] = useState<'horizontal' | 'vertical' | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldCheckMatches, setShouldCheckMatches] = useState(false);
  const [matchAnimations, setMatchAnimations] = useState<Array<{x: number, y: number, type: string}>>([]);
  
  const boardRef = useRef<HTMLDivElement>(null);
  const audioContext = useRef<AudioContext | null>(null);

  // Mobile-optimierte Sound-Effekte
  const playSound = useCallback((type: 'match' | 'swap' | 'powerup' | 'combo' | 'win' | 'lose') => {
    // Deaktiviere Sound auf sehr kleinen Bildschirmen für bessere Performance
    if (window.innerWidth < 480) return;
    
    if (!audioContext.current) {
      audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    
    const ctx = audioContext.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Mobile-optimierte Lautstärke
    const mobileVolume = window.innerWidth < 768 ? 0.05 : 0.1;
    
    switch (type) {
      case 'match':
        oscillator.frequency.setValueAtTime(800, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
        break;
      case 'swap':
        oscillator.frequency.setValueAtTime(400, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.05);
        break;
      case 'powerup':
        oscillator.frequency.setValueAtTime(300, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.2);
        break;
      case 'combo':
        oscillator.frequency.setValueAtTime(1000, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1500, ctx.currentTime + 0.15);
        break;
      case 'win':
        oscillator.frequency.setValueAtTime(523, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
        break;
      case 'lose':
        oscillator.frequency.setValueAtTime(200, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.3);
        break;
    }
    
    gainNode.gain.setValueAtTime(mobileVolume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  }, []);

  // Candy Crush Style Partikel-Effekte
  const createParticles = useCallback((x: number, y: number, type: string, count: number = 8) => {
    // Reduziere Partikel auf Mobile für bessere Performance
    const mobileCount = window.innerWidth < 768 ? Math.floor(count * 0.6) : count;
    
    const newParticles = Array.from({ length: mobileCount }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      x: x * 60 + 30, // Board-Position zu Pixel (angepasst für 7x7)
      y: y * 60 + 30,
      type,
      life: 1.0,
      // Candy Crush Style Partikel-Eigenschaften
      color: getParticleColor(type),
      size: getParticleSize(type),
      speed: getParticleSpeed(type),
      angle: (Math.PI * 2 * i) / mobileCount + Math.random() * 0.5
    }));
    
    setParticles(prev => [...prev, ...newParticles]);
    
    // Entferne Partikel nach Animation
    setTimeout(() => {
      setParticles(prev => prev.filter(p => !newParticles.find(np => np.id === p.id)));
    }, 1500);
  }, []);

  // Candy Crush Style Partikel-Farben
  const getParticleColor = (type: string): string => {
    switch (type) {
      case 'rainbow': return 'bg-gradient-to-r from-red-400 via-yellow-400 to-purple-400';
      case 'color': return 'bg-gradient-to-r from-blue-400 to-purple-400';
      case 'horizontal': return 'bg-gradient-to-r from-orange-400 to-red-400';
      case 'vertical': return 'bg-gradient-to-r from-green-400 to-blue-400';
      case 'cross': return 'bg-gradient-to-r from-yellow-400 to-orange-400';
      case 'bomb': return 'bg-gradient-to-r from-red-500 to-orange-500';
      case 'center': return 'bg-gradient-to-r from-yellow-300 to-orange-300';
      default: return 'bg-gradient-to-r from-yellow-400 to-orange-500';
    }
  };

  // Candy Crush Style Partikel-Größen
  const getParticleSize = (type: string): string => {
    switch (type) {
      case 'rainbow': return 'w-3 h-3';
      case 'color': return 'w-2 h-2';
      case 'horizontal': return 'w-1 h-3';
      case 'vertical': return 'w-3 h-1';
      case 'cross': return 'w-2 h-2';
      case 'bomb': return 'w-2 h-2';
      case 'center': return 'w-4 h-4';
      default: return 'w-2 h-2';
    }
  };

  // Candy Crush Style Partikel-Geschwindigkeiten
  const getParticleSpeed = (type: string): number => {
    switch (type) {
      case 'rainbow': return 120;
      case 'color': return 100;
      case 'horizontal': return 80;
      case 'vertical': return 80;
      case 'cross': return 90;
      case 'bomb': return 110;
      case 'center': return 150;
      default: return 100;
    }
  };

  // Initialisiere das Spielbrett
  const initializeBoard = useCallback(() => {
    const newBoard: Gem[][] = [];
    for (let y = 0; y < BOARD_SIZE; y++) {
      const row: Gem[] = [];
      for (let x = 0; x < BOARD_SIZE; x++) {
        let gemType: GemType;
        do {
          gemType = GEM_TYPES[Math.floor(Math.random() * GEM_TYPES.length)];
        } while (
          (x >= 2 && row[x-1].type === gemType && row[x-2].type === gemType) ||
          (y >= 2 && newBoard[y-1][x].type === gemType && newBoard[y-2][x].type === gemType)
        );
        
        row.push({
          id: `${x}-${y}-${Date.now()}`,
          type: gemType,
          x,
          y,
          isMatched: false,
          isPowerUp: false,
          isFalling: false,
          isNew: false,
          matchCount: 0
        });
      }
      newBoard.push(row);
    }
    setBoard(newBoard);
  }, []);

  // Power-Up direkt aktivieren
  const activatePowerUpDirectly = useCallback((x: number, y: number) => {
    const gem = board[y]?.[x];
    if (!gem?.isPowerUp) return;
    
    setIsAnimating(true);
    
    // Sound-Effekt abspielen
    playSound('powerup');
    
    // Power-Up Effekt anwenden
    const newBoard = [...board];
    const affectedGems: {x: number, y: number}[] = [];
    
    switch (gem.powerUpType) {
      case '🌈': // Super-Regenbogen: Alles zerstören
        for (let gy = 0; gy < BOARD_SIZE; gy++) {
          for (let gx = 0; gx < BOARD_SIZE; gx++) {
            affectedGems.push({x: gx, y: gy});
            createParticles(gx, gy, 'rainbow', 3);
          }
        }
        break;
        
      case '💎': // Regenbogen-Bombe: Alle gleichen Typen
        // Finde alle Früchte der gleichen Farbe wie das Power-Up
        const targetColor = gem.originalType || gem.type;
        for (let gy = 0; gy < BOARD_SIZE; gy++) {
          for (let gx = 0; gx < BOARD_SIZE; gx++) {
            const currentGem = newBoard[gy][gx];
            // Prüfe ob es die gleiche Farbe hat (auch Power-Ups mit gleichem originalType)
            if (currentGem.type === targetColor || 
                (currentGem.isPowerUp && currentGem.originalType === targetColor) ||
                (!currentGem.isPowerUp && currentGem.type === targetColor)) {
              affectedGems.push({x: gx, y: gy});
              createParticles(gx, gy, 'color', 2);
            }
          }
        }
        break;
        
      case '⚡': // Zeilen/Spalten-Bombe
        // Horizontale Explosion
        for (let gx = 0; gx < BOARD_SIZE; gx++) {
          affectedGems.push({x: gx, y});
          createParticles(gx, y, 'horizontal', 1);
        }
        // Vertikale Explosion
        for (let gy = 0; gy < BOARD_SIZE; gy++) {
          affectedGems.push({x, y: gy});
          createParticles(x, gy, 'vertical', 1);
        }
        createParticles(x, y, 'cross', 8);
        break;
        
      case '💣': // Bomben-Bombe: 3x3 Explosion
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const newX = x + dx;
            const newY = y + dy;
            if (newX >= 0 && newX < BOARD_SIZE && newY >= 0 && newY < BOARD_SIZE) {
              affectedGems.push({x: newX, y: newY});
              createParticles(newX, newY, 'bomb', 2);
            }
          }
        }
        createParticles(x, y, 'center', 12);
        break;
        
      case '🎯': // Ziel-Bombe: Kreuz-Explosion
        // Horizontale Linie
        for (let i = -2; i <= 2; i++) {
          if (x + i >= 0 && x + i < BOARD_SIZE) {
            affectedGems.push({x: x + i, y});
            createParticles(x + i, y, 'horizontal', 1);
          }
        }
        // Vertikale Linie
        for (let i = -2; i <= 2; i++) {
          if (y + i >= 0 && y + i < BOARD_SIZE) {
            affectedGems.push({x, y: y + i});
            createParticles(x, y + i, 'vertical', 1);
          }
        }
        createParticles(x, y, 'cross', 10);
        break;
    }
    
    // Früchte entfernen und Punkte vergeben
    let pointsEarned = 0;
    affectedGems.forEach(({x: gx, y: gy}) => {
      if (newBoard[gy][gx].type !== '🍎' && newBoard[gy][gx].type !== '🍊' && 
          newBoard[gy][gx].type !== '🍇' && newBoard[gy][gx].type !== '🍓' && 
          newBoard[gy][gx].type !== '🥝' && newBoard[gy][gx].type !== '🍒' && 
          newBoard[gy][gx].type !== '🍑' && newBoard[gy][gx].type !== '🥭') {
        return; // Skip if not a valid gem type
      }
      
      pointsEarned += 15;
      newBoard[gy][gx] = { 
        id: `${gx}-${gy}-${Date.now()}`,
        type: '🍎' as GemType, // Use valid gem type as placeholder
        x: gx,
        y: gy,
        isMatched: true,
        isPowerUp: false,
        isFalling: false,
        isNew: false,
        matchCount: 0
      };
    });
    
    // Combo-Bonus für Power-Up Nutzung
    const comboBonus = Math.floor(pointsEarned * 0.3);
    pointsEarned += comboBonus;
    
    setBoard(newBoard);
    setScore(prev => prev + pointsEarned);
    setCombo(prev => prev + 1);
    
    // Animation beenden und Board auffüllen
    setTimeout(() => {
      setShouldCheckMatches(true);
      setIsAnimating(false);
    }, 500);

    // Power-Up selbst entfernen (nach Benutzung)
    newBoard[y][x] = {
      id: `${x}-${y}-${Date.now()}`,
      type: '🍎' as GemType,
      x,
      y,
      isMatched: true,
      isPowerUp: false,
      isFalling: false,
      isNew: false,
      matchCount: 0
    };
  }, [board, score, combo, playSound, createParticles]);
  
  // Moderne Power-Up Erstellung
  const createPowerUp = useCallback((matches: {x: number, y: number}[], matchType: GemType): PowerUpType => {
    const matchCount = matches.length;
    
    if (matchCount >= 6) {
      return '🌈'; // Super-Regenbogen: Zerstört alle Früchte
    } else if (matchCount >= 5) {
      return '💎'; // Regenbogen-Bombe: Alle gleichen Typen
    } else if (matchCount === 4) {
      return '⚡'; // Zeilen/Spalten-Bombe
    } else if (isLShapeMatch(matches)) {
      return '💣'; // Bomben-Bombe: 3x3 Explosion
    } else if (isTShapeMatch(matches)) {
      return '🎯'; // Ziel-Bombe: Kreuz-Explosion
    }
    
    return '💣';
  }, []);

  // L-Form Erkennung
  const isLShapeMatch = useCallback((matches: {x: number, y: number}[]): boolean => {
    if (matches.length < 5) return false;
    
    const xCoords = Array.from(new Set(matches.map(m => m.x)));
    const yCoords = Array.from(new Set(matches.map(m => m.y)));
    
    return xCoords.length > 1 && yCoords.length > 1;
  }, []);

  // T-Form Erkennung
  const isTShapeMatch = useCallback((matches: {x: number, y: number}[]): boolean => {
    if (matches.length < 5) return false;
    
    const xCoords = Array.from(new Set(matches.map(m => m.x)));
    const yCoords = Array.from(new Set(matches.map(m => m.y)));
    
    return xCoords.length === 3 && yCoords.length === 3;
  }, []);

  // Candy Crush Style Power-Up Effekte mit Explosionen
  const activatePowerUp = useCallback((gem: Gem, board: Gem[][]): {x: number, y: number}[] => {
    const affectedPositions: {x: number, y: number}[] = [];
    
    switch (gem.powerUpType) {
      case '🌈': // Super-Regenbogen: Alles zerstören (wie Candy Crush Regenbogen)
        for (let y = 0; y < BOARD_SIZE; y++) {
          for (let x = 0; x < BOARD_SIZE; x++) {
            affectedPositions.push({x, y});
            // Erstelle Explosions-Partikel für jeden zerstörten Gem
            createParticles(x, y, 'rainbow', 3);
          }
        }
        playSound('powerup');
        break;
        
      case '💎': // Regenbogen-Bombe: Alle gleichen Typen (wie Candy Crush Color Bomb)
        // Finde alle Früchte der gleichen Farbe wie das Power-Up
        const targetColor = gem.originalType || gem.type;
        for (let y = 0; y < BOARD_SIZE; y++) {
          for (let x = 0; x < BOARD_SIZE; x++) {
            const currentGem = board[y][x];
            // Prüfe ob es die gleiche Farbe hat (auch Power-Ups mit gleichem originalType)
            if (currentGem.type === targetColor || 
                (currentGem.isPowerUp && currentGem.originalType === targetColor) ||
                (!currentGem.isPowerUp && currentGem.type === targetColor)) {
              affectedPositions.push({x, y});
              // Erstelle Explosions-Partikel für jeden zerstörten Gem
              createParticles(x, y, 'color', 2);
            }
          }
        }
        playSound('powerup');
        break;
        
      case '⚡': // Zeilen/Spalten-Bombe (wie Candy Crush Striped Candy)
        // Horizontale Explosion
        for (let x = 0; x < BOARD_SIZE; x++) {
          affectedPositions.push({x, y: gem.y});
          createParticles(x, gem.y, 'horizontal', 1);
        }
        // Vertikale Explosion
        for (let y = 0; y < BOARD_SIZE; y++) {
          affectedPositions.push({x: gem.x, y});
          createParticles(gem.x, y, 'vertical', 1);
        }
        // Kreuz-Explosion in der Mitte
        createParticles(gem.x, gem.y, 'cross', 8);
        playSound('powerup');
        break;
        
      case '💣': // Bomben-Bombe: 3x3 Explosion (wie Candy Crush Wrapped Candy)
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const newX = gem.x + dx;
            const newY = gem.y + dy;
            if (newX >= 0 && newX < BOARD_SIZE && newY >= 0 && newY < BOARD_SIZE) {
              affectedPositions.push({x: newX, y: newY});
              // Erstelle Explosions-Partikel für jeden zerstörten Gem
              createParticles(newX, newY, 'bomb', 2);
            }
          }
        }
        // Zentrale Explosion
        createParticles(gem.x, gem.y, 'center', 12);
        playSound('powerup');
        break;
        
      case '🎯': // Ziel-Bombe: Kreuz-Explosion (wie Candy Crush L-Form)
        // Horizontale Linie
        for (let i = -2; i <= 2; i++) {
          if (gem.x + i >= 0 && gem.x + i < BOARD_SIZE) {
            affectedPositions.push({x: gem.x + i, y: gem.y});
            createParticles(gem.x + i, gem.y, 'horizontal', 1);
          }
        }
        // Vertikale Linie
        for (let i = -2; i <= 2; i++) {
          if (gem.y + i >= 0 && gem.y + i < BOARD_SIZE) {
            affectedPositions.push({x: gem.x, y: gem.y + i});
            createParticles(gem.x, gem.y + i, 'vertical', 1);
          }
        }
        // Zentrale Explosion
        createParticles(gem.x, gem.y, 'cross', 10);
        playSound('powerup');
        break;
        
      
    }
    
    // Power-Up selbst entfernen (nach Benutzung)
    board[gem.y][gem.x] = {
      id: `${gem.x}-${gem.y}-${Date.now()}`,
      type: '🍎' as GemType,
      x: gem.x,
      y: gem.y,
      isMatched: true,
      isPowerUp: false,
      isFalling: false,
      isNew: false,
      matchCount: 0
    };

    return affectedPositions;
  }, [playSound, createParticles]);

  // Moderne Match-Erkennung
  const findMatches = useCallback((board: Gem[][]) => {
    const matches: {x: number, y: number, type: GemType}[] = [];
    
    // Horizontale Matches
    for (let y = 0; y < BOARD_SIZE; y++) {
      let count = 1;
      let currentType = board[y][0].isPowerUp ? null : board[y][0].type as GemType;
      let startX = 0;
      
      for (let x = 1; x < BOARD_SIZE; x++) {
        const gemType = board[y][x].isPowerUp ? null : board[y][x].type as GemType;
        
        if (gemType && gemType === currentType) {
          count++;
        } else {
          if (count >= 3 && currentType) {
            for (let i = startX; i < x; i++) {
              matches.push({x: i, y, type: currentType});
            }
          }
          count = 1;
          currentType = gemType;
          startX = x;
        }
      }
      
      if (count >= 3 && currentType) {
        for (let i = startX; i < BOARD_SIZE; i++) {
          matches.push({x: i, y, type: currentType});
        }
      }
    }
    
    // Vertikale Matches
    for (let x = 0; x < BOARD_SIZE; x++) {
      let count = 1;
      let currentType = board[0][x].isPowerUp ? null : board[0][x].type as GemType;
      let startY = 0;
      
      for (let y = 1; y < BOARD_SIZE; y++) {
        const gemType = board[y][x].isPowerUp ? null : board[y][x].type as GemType;
        
        if (gemType && gemType === currentType) {
          count++;
        } else {
          if (count >= 3 && currentType) {
            for (let i = startY; i < y; i++) {
              matches.push({x, y: i, type: currentType});
            }
          }
          count = 1;
          currentType = gemType;
          startY = y;
        }
      }
      
      if (count >= 3 && currentType) {
        for (let i = startY; i < BOARD_SIZE; i++) {
          matches.push({x, y: i, type: currentType});
        }
      }
    }
    
    return matches;
  }, []);

  // Candy Crush Style Match-Verarbeitung mit Explosionen
  const removeMatchesAndDrop = useCallback((board: Gem[][]) => {
    let matches = findMatches(board);
    
    if (matches.length === 0) return { newBoard: board, pointsEarned: 0 };

    // Power-Up Aktivierungen mit Kettenreaktionen
    const powerUpActivations: {x: number, y: number}[] = [];
    const activatedPowerUps: Set<string> = new Set();
    
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        if (board[y][x].isPowerUp && matches.some(m => m.x === x && m.y === y)) {
          const powerUpId = `${x}-${y}`;
          if (!activatedPowerUps.has(powerUpId)) {
            activatedPowerUps.add(powerUpId);
            const additionalMatches = activatePowerUp(board[y][x], board);
            powerUpActivations.push(...additionalMatches);
            
            // Candy Crush Style Power-Up Explosion
            const powerUpType = board[y][x].powerUpType || 'powerup';
            createParticles(x, y, powerUpType, 20);
            
            // Zusätzliche Explosions-Effekte basierend auf Power-Up Typ
            switch (powerUpType) {
              case '🌈':
                // Regenbogen-Explosion
                for (let i = 0; i < 8; i++) {
                  setTimeout(() => {
                    createParticles(x, y, 'rainbow', 5);
                  }, i * 50);
                }
                break;
              case '💎':
                // Color Bomb Explosion
                for (let i = 0; i < 6; i++) {
                  setTimeout(() => {
                    createParticles(x, y, 'color', 4);
                  }, i * 60);
                }
                break;
              case '⚡':
                // Striped Candy Explosion
                setTimeout(() => createParticles(x, y, 'horizontal', 8), 100);
                setTimeout(() => createParticles(x, y, 'vertical', 8), 200);
                break;
              case '💣':
                // Wrapped Candy Explosion
                for (let i = 0; i < 4; i++) {
                  setTimeout(() => {
                    createParticles(x, y, 'bomb', 6);
                  }, i * 80);
                }
                break;
            }
          }
        }
      }
    }
    
    // Kombiniere Matches
    const allMatches = [...matches.map(m => ({x: m.x, y: m.y})), ...powerUpActivations];
    const uniqueMatches = allMatches.filter((match, index, self) => 
      index === self.findIndex(m => m.x === match.x && m.y === match.y)
    );
    
    if (uniqueMatches.length === 0) return { newBoard: board, pointsEarned: 0 };

    const newBoard = board.map(row => [...row]);
    let pointsEarned = 0;

    // Gruppiere Matches für Power-Up Erstellung
    const matchGroups: {[key: string]: {x: number, y: number, type: GemType}[]} = {};
    matches.forEach(match => {
      const key = match.type;
      if (!matchGroups[key]) matchGroups[key] = [];
      matchGroups[key].push(match);
    });

    // Erstelle Power-Ups für große Matches (nur ab 4 Matches)
    Object.entries(matchGroups).forEach(([gemType, groupMatches]) => {
      if (groupMatches.length >= 4) { // Nur ab 4 Matches Power-Ups erstellen
        const centerX = Math.floor(groupMatches.reduce((sum, m) => sum + m.x, 0) / groupMatches.length);
        const centerY = Math.floor(groupMatches.reduce((sum, m) => sum + m.y, 0) / groupMatches.length);
        
        const powerUpType = createPowerUp(groupMatches, gemType as GemType);
        
        newBoard[centerY][centerX] = {
          id: `powerup-${centerX}-${centerY}-${Date.now()}`,
          type: powerUpType,
          x: centerX,
          y: centerY,
          isMatched: false,
          isPowerUp: true,
          powerUpType: powerUpType,
          originalType: gemType as GemType,
          isFalling: false,
          isNew: true,
          matchCount: groupMatches.length
        };
        
        const centerIndex = uniqueMatches.findIndex(m => m.x === centerX && m.y === centerY);
        if (centerIndex !== -1) {
          uniqueMatches.splice(centerIndex, 1);
        }
        
        // Verbesserte Power-Up Boni für mehr Combos (skaliert mit Level, angepasst für 7x7)
        let basePowerUpPoints = 60; // Höhere Basis-Punkte für kleineres Board
        if (level > 10) basePowerUpPoints = 75;
        if (level > 20) basePowerUpPoints = 90;
        if (level > 30) basePowerUpPoints = 110;
        
        let powerUpBonus = groupMatches.length * basePowerUpPoints;
        if (groupMatches.length >= 5) powerUpBonus += basePowerUpPoints * 2; // 5er Match Bonus
        if (groupMatches.length >= 6) powerUpBonus += basePowerUpPoints * 4; // 6er Match Bonus
        if (groupMatches.length >= 7) powerUpBonus += basePowerUpPoints * 10; // 7er Match Mega Bonus
        
        pointsEarned += powerUpBonus;
        
        // Candy Crush Style Power-Up Erstellungs-Explosion
        createParticles(centerX, centerY, powerUpType, 15);
        
        // Zusätzliche Explosions-Effekte
        setTimeout(() => createParticles(centerX, centerY, 'center', 10), 200);
        
        // Mehrfache Explosionen für große Matches
        if (groupMatches.length >= 5) {
          setTimeout(() => createParticles(centerX, centerY, 'star', 8), 400);
        }
        if (groupMatches.length >= 6) {
          setTimeout(() => createParticles(centerX, centerY, 'rainbow', 6), 600);
        }
      }
    });

    // Markiere Matches und erstelle Partikel
    uniqueMatches.forEach(({x, y}) => {
      if (y < BOARD_SIZE && x < BOARD_SIZE) {
        newBoard[y][x].isMatched = true;
        
        // Skalierte Punktevergabe basierend auf Level (angepasst für 7x7 Board)
        let basePoints = 25; // Höhere Basis-Punkte für kleineres Board
        if (level > 10) basePoints = 30;
        if (level > 20) basePoints = 35;
        if (level > 30) basePoints = 40;
        
        pointsEarned += newBoard[y][x].isPowerUp ? basePoints * 5 : basePoints;
        
        // Candy Crush Style Match-Explosion
        const gemType = newBoard[y][x].type;
        createParticles(x, y, gemType, 4);
        
        // Zusätzliche Explosions-Effekte für Power-Ups
        if (newBoard[y][x].isPowerUp) {
          setTimeout(() => createParticles(x, y, 'star', 6), 100);
        }
      }
    });

    // Moderne Fall-Mechanik mit Animationen
    for (let x = 0; x < BOARD_SIZE; x++) {
      const column = [];
      for (let y = BOARD_SIZE - 1; y >= 0; y--) {
        if (!newBoard[y][x].isMatched) {
          column.push({...newBoard[y][x], isFalling: true});
        }
      }
      
      // Fülle mit neuen Gems
      while (column.length < BOARD_SIZE) {
        column.push({
          id: `${x}-${Date.now()}-${Math.random()}`,
          type: GEM_TYPES[Math.floor(Math.random() * GEM_TYPES.length)],
          x,
          y: 0,
          isMatched: false,
          isPowerUp: false,
          isFalling: true,
          isNew: true,
          matchCount: 0
        });
      }
      
      // Setze Y-Koordinaten
      for (let i = 0; i < BOARD_SIZE; i++) {
        column[i].y = BOARD_SIZE - 1 - i;
        column[i].isFalling = false;
        newBoard[BOARD_SIZE - 1 - i][x] = column[i];
      }
    }

    return { newBoard, pointsEarned };
  }, [findMatches, activatePowerUp, createPowerUp, createParticles]);

  // Moderne Tausch-Funktion mit Zug-Begrenzung
  const swapGems = useCallback((x1: number, y1: number, x2: number, y2: number) => {
    if (isAnimating || moves <= 0) return;
    
    // Prüfe ob die Felder benachbart sind (nur horizontal oder vertikal)
    const dx = Math.abs(x1 - x2);
    const dy = Math.abs(y1 - y2);
    const isAdjacent = (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
    
    if (!isAdjacent) {
      // Ungültiger Zug - keine benachbarten Felder
      return;
    }
    
    setIsAnimating(true);
    playSound('swap');
    
    // Reduziere Züge
    setMoves(prev => prev - 1);
    
    setBoard(prevBoard => {
      const newBoard = prevBoard.map(row => [...row]);
      const temp = newBoard[y1][x1];
      newBoard[y1][x1] = newBoard[y2][x2];
      newBoard[y2][x2] = temp;
      
      newBoard[y1][x1].x = x1;
      newBoard[y1][x1].y = y1;
      newBoard[y2][x2].x = x2;
      newBoard[y2][x2].y = y2;
      
      return newBoard;
    });
    
    setTimeout(() => {
      setIsAnimating(false);
      setShouldCheckMatches(true);
    }, 300);
  }, [isAnimating, playSound, moves]);

  // Mobile-optimierte Touch/Drag Handler
  const handleMouseDown = useCallback((e: React.MouseEvent, x: number, y: number) => {
    if (gameOver || gameWon || isAnimating || moves <= 0) return;
    
    e.preventDefault();
    setIsDragging(true);
    setDragStart({x, y, clientX: e.clientX, clientY: e.clientY});
    setSelectedGem({x, y});
    
    // Prüfe ob es ein Power-Up ist
    const gem = board[y]?.[x];
    if (gem?.isPowerUp) {
      // Power-Up direkt aktivieren
      activatePowerUpDirectly(x, y);
    }
  }, [gameOver, gameWon, isAnimating, moves, board]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !dragStart || isAnimating) return;
    
    e.preventDefault();
    const deltaX = e.clientX - dragStart.clientX;
    const deltaY = e.clientY - dragStart.clientY;
    
    // Mobile-optimierte Schwellenwerte
    const minSwipeDistance = window.innerWidth < 768 ? 15 : 20;
    const triggerDistance = window.innerWidth < 768 ? 25 : 30;
    
    if (Math.abs(deltaX) > minSwipeDistance || Math.abs(deltaY) > minSwipeDistance) {
      const direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      setDragDirection(direction);
      
      if (direction === 'horizontal' && Math.abs(deltaX) > triggerDistance) {
        const newX = deltaX > 0 ? dragStart.x + 1 : dragStart.x - 1;
        if (newX >= 0 && newX < BOARD_SIZE) {
          swapGems(dragStart.x, dragStart.y, newX, dragStart.y);
          setIsDragging(false);
          setDragStart(null);
        }
      } else if (direction === 'vertical' && Math.abs(deltaY) > triggerDistance) {
        const newY = deltaY > 0 ? dragStart.y + 1 : dragStart.y - 1;
        if (newY >= 0 && newY < BOARD_SIZE) {
          swapGems(dragStart.x, dragStart.y, dragStart.x, newY);
          setIsDragging(false);
          setDragStart(null);
        }
      }
    }
  }, [isDragging, dragStart, isAnimating, swapGems]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
    setDragDirection(null);
  }, []);

  // Mobile-optimierte Touch Handler
  const handleTouchStart = useCallback((e: React.TouchEvent, x: number, y: number) => {
    if (gameOver || gameWon || isAnimating || moves <= 0) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({x, y, clientX: touch.clientX, clientY: touch.clientY});
    setSelectedGem({x, y});
    
    // Prüfe ob es ein Power-Up ist
    const gem = board[y]?.[x];
    if (gem?.isPowerUp) {
      // Power-Up direkt aktivieren
      activatePowerUpDirectly(x, y);
    }
  }, [gameOver, gameWon, isAnimating, moves, board]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || !dragStart || isAnimating) return;
    
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.clientX;
    const deltaY = touch.clientY - dragStart.clientY;
    
    // Mobile-optimierte Schwellenwerte
    const minSwipeDistance = 15;
    const triggerDistance = 25;
    
    if (Math.abs(deltaX) > minSwipeDistance || Math.abs(deltaY) > minSwipeDistance) {
      const direction = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      setDragDirection(direction);
      
      if (direction === 'horizontal' && Math.abs(deltaX) > triggerDistance) {
        const newX = deltaX > 0 ? dragStart.x + 1 : dragStart.x - 1;
        if (newX >= 0 && newX < BOARD_SIZE) {
          swapGems(dragStart.x, dragStart.y, newX, dragStart.y);
          setIsDragging(false);
          setDragStart(null);
        }
      } else if (direction === 'vertical' && Math.abs(deltaY) > triggerDistance) {
        const newY = deltaY > 0 ? dragStart.y + 1 : dragStart.y - 1;
        if (newY >= 0 && newY < BOARD_SIZE) {
          swapGems(dragStart.x, dragStart.y, dragStart.x, newY);
          setIsDragging(false);
          setDragStart(null);
        }
      }
    }
  }, [isDragging, dragStart, isAnimating, swapGems]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setDragStart(null);
    setDragDirection(null);
  }, []);

  // Moderne Match-Verarbeitung mit verbesserten Combos
  useEffect(() => {
    if (!shouldCheckMatches || board.length === 0) return;

    let isProcessing = true;
    let currentCombo = 0;
    let totalPointsInCombo = 0;
    
    const processMatches = (currentBoard: Gem[][]) => {
      if (!isProcessing) return;
      
      const { newBoard, pointsEarned } = removeMatchesAndDrop(currentBoard);
      if (pointsEarned > 0) {
        currentCombo++;
        totalPointsInCombo += pointsEarned;
        setCombo(currentCombo);
        setMaxCombo(prev => Math.max(prev, currentCombo));
        
        // Verbesserte Combo-Multiplikatoren für mehr Combos
        const comboMultiplier = Math.min(currentCombo * 1.5, 10); // Höhere Multiplikatoren
        const finalPoints = Math.floor(pointsEarned * comboMultiplier);
        
        // Zusätzliche Combo-Boni (skaliert mit Level)
        let baseComboBonus = 50;
        if (level > 10) baseComboBonus = 75;
        if (level > 20) baseComboBonus = 100;
        if (level > 30) baseComboBonus = 150;
        
        let bonusPoints = 0;
        if (currentCombo >= 3) bonusPoints += baseComboBonus; // 3er Combo Bonus
        if (currentCombo >= 5) bonusPoints += baseComboBonus * 2; // 5er Combo Bonus
        if (currentCombo >= 7) bonusPoints += baseComboBonus * 4; // 7er Combo Bonus
        if (currentCombo >= 10) bonusPoints += baseComboBonus * 8; // 10er Combo Mega Bonus
        
        const totalPoints = finalPoints + bonusPoints;
        
        setScore(prev => prev + totalPoints);
        setBoard(newBoard);
        
        // Verbesserte Sound-Effekte für Combos
        playSound('match');
        if (currentCombo > 1) {
          playSound('combo');
          // Zusätzliche Combo-Sounds für hohe Combos
          if (currentCombo >= 5) {
            setTimeout(() => playSound('powerup'), 100);
          }
          if (currentCombo >= 8) {
            setTimeout(() => playSound('powerup'), 200);
          }
        }
        
        // Schnellere Verarbeitung für mehr Combos
        setTimeout(() => processMatches(newBoard), currentCombo > 5 ? 200 : 300);
      } else {
        // Combo-Ende: Zeige finale Combo-Punkte
        if (currentCombo > 1) {
          const comboBonus = Math.floor(totalPointsInCombo * (currentCombo - 1) * 0.2);
          setScore(prev => prev + comboBonus);
        }
        
        setShouldCheckMatches(false);
        setCombo(0);
        setIsAnimating(false);
        
        // Prüfe ob noch Züge übrig sind
        if (moves <= 0) {
          setGameOver(true);
          playSound('lose');
        }
      }
    };

    const timeoutId = setTimeout(() => processMatches(board), 150); // Schnellere Start
    
    return () => {
      isProcessing = false;
      clearTimeout(timeoutId);
    };
  }, [shouldCheckMatches, board, removeMatchesAndDrop, playSound, moves]);

  // Spielende-Prüfung
  useEffect(() => {
    if (score >= targetScore && !gameWon) {
      setGameWon(true);
      playSound('win');
      completeMatchThreeLevel(level, score);
      addGems(level * 3); // Mehr Belohnungen
      addCoins(score * 2);
    } else if (moves <= 0 && !gameWon && !gameOver) {
      setGameOver(true);
      playSound('lose');
    }
  }, [score, moves, targetScore, gameWon, gameOver, level, completeMatchThreeLevel, addGems, addCoins, playSound]);

  // Initialisiere Spiel
  useEffect(() => {
    if (isOpen) {
      initializeBoard();
      setScore(0);
      setMoves(maxMoves);
      setGameOver(false);
      setGameWon(false);
      setSelectedGem(null);
      setCombo(0);
      setMaxCombo(0);
      setParticles([]);
    }
  }, [isOpen, initializeBoard, maxMoves]);

  const resetGame = () => {
    initializeBoard();
    setScore(0);
    setMoves(maxMoves);
    setGameOver(false);
    setGameWon(false);
    setSelectedGem(null);
    setCombo(0);
    setMaxCombo(0);
    setParticles([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 max-w-2xl w-full mx-2 sm:mx-4 max-h-[98vh] sm:max-h-[95vh] overflow-y-auto shadow-2xl border border-purple-200"
      >
        {/* Mobile-optimierter Header */}
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent leading-tight">
              Garten-Match Level {level}
            </h2>
            <div className="text-xs sm:text-sm text-gray-600">Match 3 oder mehr Früchte!</div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 rounded-full hover:bg-gray-100 flex items-center justify-center transition-all touch-manipulation"
          >
            ×
          </button>
        </div>

        {/* Mobile-optimierte Statistiken */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border border-purple-300">
            <div className="text-xs sm:text-sm text-purple-700 font-medium">Punkte</div>
            <div className="text-lg sm:text-2xl font-bold text-purple-800">{score.toLocaleString()}</div>
          </div>
          <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border border-green-300">
            <div className="text-xs sm:text-sm text-green-700 font-medium">Ziel</div>
            <div className="text-lg sm:text-2xl font-bold text-green-800">{targetScore.toLocaleString()}</div>
          </div>
          <div className="bg-gradient-to-br from-red-100 to-red-200 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border border-red-300">
            <div className="text-xs sm:text-sm text-red-700 font-medium">Züge</div>
            <div className="text-lg sm:text-2xl font-bold text-red-800">{moves}</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl sm:rounded-2xl p-2 sm:p-4 text-center border border-yellow-300">
            <div className="text-xs sm:text-sm text-yellow-700 font-medium">Combo</div>
            <div className="text-lg sm:text-2xl font-bold text-yellow-800">{combo}x</div>
          </div>
        </div>

        {/* Mobile-optimierter Fortschrittsbalken */}
        <div className="mb-4 sm:mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600"
              initial={{ width: 0 }}
              animate={{ width: `${Math.min((score / targetScore) * 100, 100)}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <div className="text-center text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">
            {Math.round((score / targetScore) * 100)}% zum Ziel
          </div>
        </div>



        {/* Mobile-optimiertes Spielbrett */}
        <div 
          ref={boardRef}
          className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-4 sm:mb-6 aspect-square max-w-sm sm:max-w-lg mx-auto select-none bg-gradient-to-br from-white to-gray-50 rounded-2xl sm:rounded-3xl p-2 sm:p-4 border-2 border-purple-200 shadow-xl touch-manipulation"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            touchAction: 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none'
          }}
        >
          {board.map((row, y) =>
            row.map((gem, x) => (
                              <motion.button
                  key={gem.id}
                  onMouseDown={(e) => handleMouseDown(e, x, y)}
                  onTouchStart={(e) => handleTouchStart(e, x, y)}
                  whileHover={{ scale: window.innerWidth >= 768 ? 1.1 : 1 }}
                  whileTap={{ scale: 0.9 }}
                                  animate={{
                  scale: gem.isMatched ? [1, 1.3, 0] : 1,
                  // Candy Crush Style Explosions-Animation
                  filter: gem.isMatched ? ['brightness(1)', 'brightness(2)', 'brightness(0)'] : 'brightness(1)',
                  boxShadow: gem.isMatched ? [
                    '0 0 0 rgba(255,255,255,0)',
                    '0 0 20px rgba(255,255,255,0.8)',
                    '0 0 0 rgba(255,255,255,0)'
                  ] : '0 0 0 rgba(255,255,255,0)'
                }}
                transition={{
                  duration: gem.isMatched ? 0.4 : 0.2,
                  ease: "easeInOut"
                }}
                  className={`
                    aspect-square text-lg sm:text-2xl rounded-xl sm:rounded-2xl border-2 transition-all duration-200 cursor-pointer relative overflow-hidden touch-manipulation
                    ${gem.isPowerUp 
                      ? 'border-purple-500 bg-gradient-to-br from-purple-100 to-pink-100 shadow-lg animate-pulse' 
                      : selectedGem?.x === x && selectedGem?.y === y 
                        ? 'border-yellow-400 bg-gradient-to-br from-yellow-100 to-orange-100 shadow-lg scale-110' 
                        : 'border-gray-300 bg-gradient-to-br from-white to-gray-50 hover:from-gray-50 hover:to-gray-100'
                    }

                    ${isDragging && dragStart?.x === x && dragStart?.y === y 
                      ? 'scale-125 shadow-2xl border-blue-400 bg-gradient-to-br from-blue-100 to-purple-100' 
                      : ''
                    }
                  `}
                  disabled={gameOver || gameWon || isAnimating || moves <= 0}
                  style={{
                    touchAction: 'none',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTouchCallout: 'none'
                  }}
                  title={gem.isPowerUp ? `Power-Up: ${gem.type} - Wischen zum Aktivieren!` : undefined}
                >
                <div className="relative z-10">{gem.type}</div>
                {gem.isPowerUp && (
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 to-pink-400/20 animate-pulse rounded-2xl" />
                )}

              </motion.button>
            ))
          )}
        </div>

        {/* Candy Crush Style Partikel-Effekte */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {particles.map(particle => (
            <motion.div
              key={particle.id}
              className={`absolute ${particle.color} ${particle.size} rounded-full shadow-lg`}
              initial={{ 
                x: particle.x, 
                y: particle.y, 
                scale: 1, 
                opacity: 1,
                rotate: 0
              }}
              animate={{ 
                x: particle.x + Math.cos(particle.angle) * particle.speed,
                y: particle.y + Math.sin(particle.angle) * particle.speed,
                scale: [1, 1.5, 0],
                opacity: [1, 0.8, 0],
                rotate: [0, 180, 360]
              }}
              transition={{ 
                duration: 1.5,
                ease: "easeOut"
              }}
            />
          ))}
        </div>

        {/* Mobile-optimierte Combo-Anzeige */}
        {combo > 1 && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-4xl sm:text-6xl font-bold text-yellow-500 drop-shadow-lg pointer-events-none z-50"
          >
            {combo}x COMBO!
          </motion.div>
        )}

        {/* Mobile-optimierte Spielende-Modals */}
        <AnimatePresence key="match-three-game-over-modal">
          {(gameWon || gameOver) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-2xl sm:rounded-3xl"
            >
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-8 text-center shadow-2xl border border-purple-200 mx-4 max-w-sm sm:max-w-md"
              >
                                 {gameWon ? (
                   <div className="text-green-600">
                     <motion.div 
                       initial={{ scale: 0 }}
                       animate={{ scale: 1 }}
                       transition={{ delay: 0.2, type: "spring" }}
                       className="text-4xl sm:text-6xl mb-3 sm:mb-4"
                     >
                       🎉
                     </motion.div>
                     <div className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Level Geschafft!</div>
                     <div className="text-base sm:text-lg mb-4 sm:mb-6">
                       Belohnung: {level * 3} 💎 + {score * 2} 💰
                     </div>
                     <div className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                       Max Combo: {maxCombo}x | Punkte: {score.toLocaleString()}
                     </div>
                   </div>
                 ) : (
                   <div className="text-red-600">
                     <motion.div 
                       initial={{ scale: 0 }}
                       animate={{ scale: 1 }}
                       transition={{ delay: 0.2, type: "spring" }}
                       className="text-4xl sm:text-6xl mb-3 sm:mb-4"
                     >
                       😔
                     </motion.div>
                     <div className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Keine Züge mehr!</div>
                     <div className="text-base sm:text-lg mb-4 sm:mb-6">Versuche es nochmal!</div>
                   </div>
                 )}
                
                                 <div className="flex gap-3 sm:gap-4 justify-center">
                   <motion.button 
                     onClick={resetGame}
                     whileHover={{ scale: window.innerWidth >= 768 ? 1.05 : 1 }}
                     whileTap={{ scale: 0.95 }}
                     className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all touch-manipulation text-sm sm:text-base"
                   >
                     Nochmal
                   </motion.button>
                   <motion.button 
                     onClick={onClose}
                     whileHover={{ scale: window.innerWidth >= 768 ? 1.05 : 1 }}
                     whileTap={{ scale: 0.95 }}
                     className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all touch-manipulation text-sm sm:text-base"
                   >
                     Schließen
                   </motion.button>
                 </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}; 
